/* LOGIN TRACKING (Ale 2026-09-02) — the admin's logins view.
 *
 * Two tables, every row a SENTENCE with the record values as DATA
 * (Sentence.tsx, the datum canon): who is signed in now and from where,
 * and every sign-in attempt newest first — refused ones say why. A live
 * session carries one door: end it. Admin-only (the route gates it and
 * the endpoints 403 anyone else).
 *
 * The page's own heartbeat re-fetches as BACKGROUND: an admin leaving
 * this tab open is not activity (lib/api.ts). */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../../lib/api'
import { ageWords } from '../../lib/sentences/compile'
import Sentence from '../Sentence'
import { endedWords, refusalWords, whenParts, type EndedBy, type Outcome } from './loginWords'
import './logins.css'

export interface SessionSummary {
  id: string
  user_id: string
  username: string
  display_name: string
  created_at: string
  last_seen_at: string
  revoked_at: string | null
  ended_by: EndedBy | null
  ended_at: string | null
  live: boolean
  ip: string | null
  user_agent: string | null
  client: string
}

export interface LoginRow {
  id: string
  at: string
  username: string
  user_id: string | null
  display_name: string | null
  outcome: Outcome
  ip: string | null
  user_agent: string | null
  client: string
  session: SessionSummary | null
}

interface LoginsResponse {
  items: LoginRow[]
  idle_hours: number
}
interface SessionsResponse {
  items: SessionSummary[]
  idle_hours: number
}

/** How long a session lasted (or has lasted), in the age words the
 *  queue rows use — "22 minutes", "3 hours", "moments". */
function lengthWords(s: SessionSummary, now: number): string | null {
  const end = s.ended_at ? new Date(s.ended_at).getTime() : now
  return ageWords(s.created_at, end)
}

/* ── rows ─────────────────────────────────────────────────────────── */

function SessionRow({
  s,
  idleHours,
  now,
  onRevoke,
}: {
  s: SessionSummary
  idleHours: number
  now: number
  onRevoke: (id: string) => Promise<void>
}) {
  const [arming, setArming] = useState(false)
  const [busy, setBusy] = useState(false)
  const { day, time } = whenParts(s.created_at)
  const values = {
    user: s.username,
    ip: s.ip,
    day,
    time,
    client: s.client,
    age: ageWords(s.last_seen_at, now),
    length: lengthWords(s, now),
    ended: endedWords(s.ended_by, idleHours),
  }
  return (
    <div className={`lg-row${s.live ? ' live' : ''}`} data-testid="session-row" data-id={s.id}>
      <Sentence
        meaning={s.live ? 'session.live' : 'session.ended'}
        values={values}
        className="sn-lead"
        data-testid="session-sentence"
      />
      {s.live && !arming && (
        <button
          type="button"
          className="lg-door"
          onClick={() => setArming(true)}
          data-testid={`session-revoke-${s.id}`}
        >
          End session
        </button>
      )}
      {s.live && arming && (
        <span className="lg-confirm">
          <span>End this session now?</span>
          <button
            type="button"
            className="lg-door primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true)
              try {
                await onRevoke(s.id)
              } finally {
                setBusy(false)
                setArming(false)
              }
            }}
            data-testid={`session-revoke-confirm-${s.id}`}
          >
            {busy ? 'Ending…' : 'Yes, end it'}
          </button>
          <button
            type="button"
            className="lg-door quiet"
            disabled={busy}
            onClick={() => setArming(false)}
          >
            Keep it
          </button>
        </span>
      )}
    </div>
  )
}

function LoginRowView({ r, idleHours, now }: { r: LoginRow; idleHours: number; now: number }) {
  const { day, time } = whenParts(r.at)
  const ok = r.outcome === 'ok'
  const s = r.session
  return (
    <div
      className={`lg-row${ok ? '' : ' refused'}`}
      data-testid="login-row"
      data-outcome={r.outcome}
    >
      <Sentence
        meaning={ok ? 'login.signed_in' : 'login.refused'}
        values={{
          user: r.username,
          ip: r.ip,
          day,
          time,
          client: r.client,
          reason: ok ? null : refusalWords(r.outcome),
        }}
        className="sn-lead"
        data-testid="login-sentence"
      />
      {ok && s && (
        <Sentence
          meaning={s.live ? 'login.session_live' : 'login.session_ended'}
          values={{ length: lengthWords(s, now), ended: endedWords(s.ended_by, idleHours) }}
          className="lg-tail"
        />
      )}
    </div>
  )
}

/* ── the page ─────────────────────────────────────────────────────── */

export default function LoginsPage() {
  const [logins, setLogins] = useState<LoginRow[] | 'error' | null>(null)
  const [sessions, setSessions] = useState<SessionSummary[] | 'error' | null>(null)
  const [idleHours, setIdleHours] = useState(4)
  const [now, setNow] = useState(() => Date.now())
  const [refusedOnly, setRefusedOnly] = useState(false)

  const load = useCallback((background = false) => {
    const bg = background === true
    apiFetch<SessionsResponse>('/api/admin/sessions', { background: bg })
      .then((d) => {
        setSessions(d.items)
        setIdleHours(d.idle_hours)
      })
      .catch(() => setSessions('error'))
    apiFetch<LoginsResponse>('/api/admin/logins?limit=200', { background: bg })
      .then((d) => setLogins(d.items))
      .catch(() => setLogins('error'))
    setNow(Date.now())
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(() => load(true), 30000)
    return () => clearInterval(t)
  }, [load])

  const revoke = useCallback(
    async (id: string) => {
      try {
        await apiFetch(`/api/admin/sessions/${id}/revoke`, { method: 'POST' })
      } finally {
        load()
      }
    },
    [load]
  )

  const live = useMemo(
    () => (Array.isArray(sessions) ? sessions.filter((s) => s.live) : []),
    [sessions]
  )
  const ended = useMemo(
    () => (Array.isArray(sessions) ? sessions.filter((s) => !s.live) : []),
    [sessions]
  )
  const attempts = useMemo(
    () =>
      Array.isArray(logins)
        ? refusedOnly
          ? logins.filter((r) => r.outcome !== 'ok')
          : logins
        : [],
    [logins, refusedOnly]
  )

  return (
    <div className="logins" data-testid="logins-page">
      <p className="lg-intro">
        Every sign-in attempt is on record. A session ends when the officer signs out, after{' '}
        <b className="sn-datum" data-kind="count">
          {idleHours} hours
        </b>{' '}
        without activity, when its token expires, or when an administrator ends it here.
      </p>

      <section className="lg-section" data-testid="sessions-section">
        <div className="lg-head">
          <h2>Signed in now</h2>
          <button type="button" className="lg-door quiet" onClick={() => load()}>
            Refresh
          </button>
        </div>
        {sessions === null && <div className="lg-empty">Loading…</div>}
        {sessions === 'error' && <div className="lg-empty">The sessions could not be loaded.</div>}
        {Array.isArray(sessions) && live.length === 0 && (
          <div className="lg-empty">Nobody is signed in now.</div>
        )}
        {live.map((s) => (
          <SessionRow key={s.id} s={s} idleHours={idleHours} now={now} onRevoke={revoke} />
        ))}
        {ended.length > 0 && (
          <>
            <h3 className="lg-sub">Ended in the last 24 hours</h3>
            {ended.map((s) => (
              <SessionRow key={s.id} s={s} idleHours={idleHours} now={now} onRevoke={revoke} />
            ))}
          </>
        )}
      </section>

      <section className="lg-section" data-testid="logins-section">
        <div className="lg-head">
          <h2>Sign-in attempts</h2>
          <label className="lg-toggle">
            <input
              type="checkbox"
              checked={refusedOnly}
              onChange={(e) => setRefusedOnly(e.target.checked)}
            />
            refused only
          </label>
        </div>
        {logins === null && <div className="lg-empty">Loading…</div>}
        {logins === 'error' && (
          <div className="lg-empty">The sign-in attempts could not be loaded.</div>
        )}
        {Array.isArray(logins) && attempts.length === 0 && (
          <div className="lg-empty">
            {refusedOnly ? 'No refused attempts on record.' : 'No sign-in attempts on record.'}
          </div>
        )}
        {attempts.map((r) => (
          <LoginRowView key={r.id} r={r} idleHours={idleHours} now={now} />
        ))}
      </section>
    </div>
  )
}
